import React from 'react';
import { AxiosError } from 'axios';
import { Switch, Route, Router } from 'react-router';
import { createMemoryHistory } from 'history';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ReactModal from 'react-modal';

import BankSelect from './containers/BankSelect/BankSelectPage';
import ErrorContainer from './containers/error/ErrorContainer';
import { recaptchaRef } from './di/configureContainer';
import ProviderSelectionPage from './containers/providerSelection/ProviderSelectionPage';
import ContentLayout from './components/app/ContentLayout';
import ConsentPage from './containers/consent/ConsentPage';
import ConnectionSuccessPage from './containers/connectionSuccess/ConnectionSuccessPage';
import LoaderContainer from './containers/loader/LoaderContainer';
import Recaptcha from './containers/common/Recaptcha';
import InitialScreen from './components/app/InitialScreen';
import ReconfirmationPage from './containers/reconfirmation/ReconfirmationPage';
import ReconfirmationStagePage from './containers/reconfirmation/ReconfirmationStagePage';
import application from '../config/app/config';
import './global.scss';
import AccountDisconnectionPage from './containers/reconfirmation/AccountDisconnectionPage';
import { useErrorState } from './hooks/zustandHooks';
import WelcomeScreen from './containers/welcomeScreen/WelcomeScreen';
import OTP from './containers/otp/OTP';
import VerifyNumber from './containers/otp/VerifyNumber';
import TuSuccessPage from './containers/tuSuccessPage/TuSuccessPage';
import KbaPage from './containers/kba/KbaPage';
import AccountLookup from './containers/accountLookup/AccountLookup';
import { DeviceRisk } from './containers/common/DeviceRisk';
import { createApplicationInsightsTracker } from './utils/applicationInsights';
import { applicationInsights } from './domain/services';
import DocumentUplaod from './containers/documentUpload/DocumentUpload';
import DocumentUplaodSuccess from './components/endResultStage/DocumentsUploadSuccess';

// Make sure to bind modal to your appElement (https://reactcommunity.org/react-modal/accessibility/)
if (document.getElementById('root')) {
  ReactModal.setAppElement('#root');
}

const history = createMemoryHistory();

export const { trackEvent } = createApplicationInsightsTracker(applicationInsights);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity
    }
  },
  queryCache: new QueryCache({
    onError(error, query) {
      const setError = useErrorState.getState().setError;
      const errorCode = query.options?.meta?.error_code as string | undefined;

      const err = error as AxiosError;
      const data = err?.response?.data;

      if (query.queryKey.includes('consent_info')) {
        history.push('/consent');
        return;
      }

      if ('message' in error && typeof error.message === 'string') {
        setError({ code: error.message });
      }

      if (errorCode) {
        setError({ code: errorCode });
      }

      if (data && typeof data === 'object' && 'code' in data) {
        setError({ code: data?.code as string });
      }

      history.push('/error');
    }
  }),
  mutationCache: new MutationCache({
    onError(error) {
      const err = error as AxiosError;
      const data = err.response?.data;
      if (data && typeof data === 'object' && 'code' in data) {
        useErrorState.getState().setError({ code: data?.code as string });
        history.push('/error');
      }
    }
  })
});

const App: React.FC = () => {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <Router history={history}>
          <ContentLayout>
            <Switch>
              <Route exact path="/" component={InitialScreen} />
              <Route exact path="/welcome-screen" component={WelcomeScreen} />
              <Route exact path="/account-lookup" component={AccountLookup} />
              <Route exact path="/bank-select" component={BankSelect} />
              <Route exact path="/error" component={ErrorContainer} />
              <Route exact path="/end-success" component={ConnectionSuccessPage} />
              <Route exact path="/tu-end-success" component={TuSuccessPage} />
              <Route exact path="/document-uplaod-success" component={DocumentUplaodSuccess} />
              <Route exact path="/provider-selection" component={ProviderSelectionPage} />
              {/* <Route exact path="/payment-account-confirmation-summary" component={PaymentAccountConfirmationSummaryPage} /> */}
              <Route exact path="/consent" component={ConsentPage} />
              <Route exact path="/reconfirmation" component={ReconfirmationPage} />
              <Route exact path="/reconfirmation-stage" component={ReconfirmationStagePage} />
              <Route exact path="/account-disconnection" component={AccountDisconnectionPage} />
              <Route exact path="/verify-number" component={VerifyNumber} />
              <Route exact path="/otp" component={OTP} />
              <Route exact path="/kba" component={KbaPage} />
              <Route exact path="/document-upload" component={DocumentUplaod} />
            </Switch>
          </ContentLayout>
        </Router>
        <Recaptcha size={application.recaptcha.SIZE} sitekey={application.recaptcha.SITE_KEY} ref={recaptchaRef} />
        <DeviceRisk />
        <LoaderContainer />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
};

export default App;
